'use client';
import React, { useEffect, useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import { Calendar, ArrowRight, Loader2, Newspaper } from 'lucide-react';
import Link from 'next/link';
import NewsletterForm from '@/components/NewsletterForm';

interface NewsItem {
  id: string;
  category: string | null;
  title: string;
  excerpt: string | null;
  date: string;
  author: string | null;
  image: string | null;
  imageAlt: string | null;
  featured: boolean;
  readTime: string | null;
  slug: string;
}

const categoryColors: Record<string, string> = {
  'New Release': 'bg-primary/15 text-primary',
  'Tour': 'bg-info/15 text-blue-400',
  'Interview': 'bg-warning/15 text-warning',
  'Behind the Scenes': 'bg-positive/15 text-positive',
};

export default function NewsSection() {
  const [newsItems, setNewsItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/news?limit=3')
      .then((r) => r.json())
      .then((data) => setNewsItems(data.posts ?? []))
      .catch(() => setNewsItems([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = newsItems[0];
  const secondary = newsItems.slice(1, 3);

  return (
    <section id="news" className="py-24 bg-background">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-[11px] font-600 tracking-widest uppercase text-primary mb-2">Latest</p>
            <h2 className="text-4xl font-800 text-foreground">News &amp; Updates</h2>
          </div>
          <Link href="/news" className="hidden sm:flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors">
            All News <ArrowRight size={14} />
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={32} className="animate-spin text-primary" />
          </div>
        ) : newsItems.length === 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7 flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Newspaper size={40} className="mb-4 opacity-40" />
              <p className="text-base font-600 text-foreground mb-2">No news yet</p>
              <p className="text-sm">Check back soon for the latest updates.</p>
            </div>
            <div className="lg:col-span-5">
              <NewsletterForm />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Featured news */}
            {featured && (
              <div className="lg:col-span-7 group cursor-pointer">
                <Link href={`/news/${featured.slug}`}>
                  <div className="rounded-2xl overflow-hidden bg-card border border-border card-glow-hover transition-all duration-300">
                    {featured.image && (
                      <div className="relative h-64 overflow-hidden">
                        <AppImage
                          src={featured.image}
                          alt={featured.imageAlt ?? featured.title}
                          fill
                          className="object-cover group-hover:scale-105 transition-transform duration-700"
                          sizes="(max-width: 1024px) 100vw, 60vw"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        {featured.category && (
                          <span className={`px-2.5 py-1 text-[10px] font-700 rounded-full ${categoryColors[featured.category] ?? 'bg-muted text-muted-foreground'}`}>
                            {featured.category}
                          </span>
                        )}
                        {featured.readTime && <span className="text-xs text-muted-foreground">{featured.readTime}</span>}
                      </div>
                      <h3 className="text-xl font-700 text-foreground mb-2 group-hover:text-primary transition-colors leading-snug">
                        {featured.title}
                      </h3>
                      {featured.excerpt && (
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{featured.excerpt}</p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Calendar size={12} />
                        {featured.date}
                        {featured.author && (
                          <>
                            <span className="text-border">·</span>
                            {featured.author}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            )}

            {/* Secondary news + newsletter */}
            <div className="lg:col-span-5 flex flex-col gap-4">
              {secondary.map((item) => (
                <Link key={item.id} href={`/news/${item.slug}`} className="group flex gap-4 bg-card border border-border rounded-xl p-4 card-glow-hover transition-all duration-200 cursor-pointer">
                  {item.image && (
                    <div className="relative w-24 h-24 rounded-lg overflow-hidden shrink-0">
                      <AppImage
                        src={item.image}
                        alt={item.imageAlt ?? item.title}
                        fill
                        className="object-cover group-hover:scale-110 transition-transform duration-500"
                        sizes="96px"
                      />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    {item.category && (
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <span className={`px-2 py-0.5 text-[9px] font-700 rounded-full ${categoryColors[item.category] ?? 'bg-muted text-muted-foreground'}`}>
                          {item.category}
                        </span>
                      </div>
                    )}
                    <h3 className="text-sm font-600 text-foreground group-hover:text-primary transition-colors leading-snug line-clamp-2 mb-2">
                      {item.title}
                    </h3>
                    <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                      <Calendar size={10} />
                      {item.date}
                    </div>
                  </div>
                </Link>
              ))}

              {/* Newsletter CTA */}
              <NewsletterForm />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
